import type { AudioFrame } from "@/utils/arcade/beat";
import { Fx } from "./fx";
import {
  DRAW_DISTANCE,
  FOG_END,
  FOG_START,
  LANES,
  MAX_LIVES,
  PLAYER_AHEAD,
  PLAYER_SIZE,
  ROAD_WIDTH,
  SECTOR_LENGTH,
  SEGMENT,
  createDriveSim,
  missionRewardLabel,
  playerFootprint,
  type DriveCar,
  type DriveControls,
  type DriveHazard,
  type DriveSim,
  type DriveState,
} from "./driveSim";
import { createStars, drawBeatFlash, drawCoverSun, drawGlow, drawStars, withAlpha, type Ctx2D } from "./render";
import { createFeedbackQueue, createSpawnWatcher, type FrameInput, type GameEngine, type GameEnv, type GameStatus } from "./types";

// Night Drive. The race itself lives in driveSim.ts. The world is drawn by the
// WebGL view (nightDrive3d.ts, loaded on demand with three.js) when it is
// available, and by the 2D pseudo 3D road below when it is not.

export interface DriveView {
  render(ctx: Ctx2D, audio: AudioFrame, width: number, height: number): void;
  dispose(): void;
}

export type DriveViewFactory = (env: GameEnv, sim: DriveSim) => DriveView | null;

let viewFactory: DriveViewFactory | null = null;

export function registerDriveView(factory: DriveViewFactory | null) {
  viewFactory = factory;
}

export function hasDriveView() {
  return viewFactory !== null;
}

// Taps above this share of the screen fire the nitro, so a thumb can steer
// below it without setting it off.
export const NITRO_ZONE = 0.3;
const LEFT_KEYS = new Set(["ArrowLeft", "KeyA"]);
const RIGHT_KEYS = new Set(["ArrowRight", "KeyD"]);
const BOOST_KEYS = new Set(["Space", "ArrowUp", "KeyW", "Enter"]);
const BRAKE_KEYS = new Set(["ArrowDown", "KeyS"]);

// Turns what the stage heard into what the car does.
export function controlsFrom(input: FrameInput, width: number, height: number): DriveControls {
  let steer = 0;
  let brake = false;
  for (const code of input.held) {
    if (LEFT_KEYS.has(code)) steer -= 1;
    if (RIGHT_KEYS.has(code)) steer += 1;
    if (BRAKE_KEYS.has(code)) brake = true;
  }
  // Nitro on touch: a second finger while steering, or two at once.
  let boost = input.taps.length >= 2 || input.taps.some((tap) => tap.secondary);
  for (const code of input.pressed) if (BOOST_KEYS.has(code)) boost = true;
  for (const tap of input.taps) if (tap.y < height * NITRO_ZONE) boost = true;
  let aim: number | null = null;
  const pointer = input.pointer;
  if (pointer && (pointer.down || pointer.moved) && pointer.y >= height * NITRO_ZONE && input.taps.length < 2) {
    aim = ((pointer.x / Math.max(1, width)) * 2 - 1) * 1.1;
  }
  return { steer, aim, boost, brake };
}

const CRASH_TEXT = { car: "CRASH", barrier: "CRASH", roadworks: "ROADWORKS!", pedestrian: "WATCH THE CROSSING" } as const;

export function createNightDrive(env: GameEnv): GameEngine {
  const { theme, rng, difficulty } = env;
  const { palette } = theme;
  const fx = new Fx(rng, env.reducedMotion, env.art, env.quiet);
  const sim = createDriveSim(theme, rng, difficulty);
  const { state } = sim;
  let width = env.width;
  let height = env.height;
  const view = viewFactory ? viewFactory(env, sim) : null;
  const flat = view ? null : createFlatRoad(env, sim);
  const watcher = createSpawnWatcher();
  const feedbackQueue = createFeedbackQueue();
  let missionFlash = 0;

  const carScreenY = () => height * 0.84;
  // Callouts take turns on three lines so two at once never overlap.
  let calloutLine = 0;
  const say = (label: string, color: string, size = 15) => {
    fx.text(width / 2, height * (0.6 - (calloutLine % 3) * 0.06), label, color, Math.min(size, width * 0.045));
    calloutLine += 1;
  };

  // debug() hands the race state to the playtests and the dev console.
  const engine: GameEngine & { debug(): DriveState } = {
    debug: () => state,
    bloom: view ? 0.45 : 0.4,

    resize(nextWidth, nextHeight) {
      width = nextWidth;
      height = nextHeight;
    },

    autopilot(audio) {
      const plan = sim.autopilot(audio);
      const x = width / 2 + ((plan.aim ?? 0) / 1.1) * (width / 2);
      const y = height * 0.85;
      return {
        taps: [],
        pointer: { x, y, down: true, moved: true },
        pressed: plan.boost ? new Set(["Space"]) : new Set<string>(),
        held: plan.brake ? new Set(["ArrowDown"]) : new Set<string>(),
      };
    },

    update(dt, input: FrameInput, audio) {
      sim.update(dt, controlsFrom(input, width, height), audio);
      const carY = carScreenY();
      missionFlash = Math.max(0, missionFlash - dt);
      for (const event of state.events) {
        if (event.type === "crash") {
          feedbackQueue.push("crash");
          fx.kick(14);
          fx.burst(width / 2, carY, palette.danger, 26, 280);
          fx.ring(width / 2, carY, palette.danger, 120);
          say(CRASH_TEXT[event.cause], palette.danger, 20);
        } else if (event.type === "bump") {
          feedbackQueue.push("hurt");
          fx.kick(6);
          fx.burst(width / 2, carY, "hsl(28 100% 55%)", 10, 200);
          say("CONE", "hsl(28 100% 60%)");
        } else if (event.type === "slide") {
          feedbackQueue.push("tap");
          say("SLIDING", palette.accent);
        } else if (event.type === "pickup") {
          fx.burst(width / 2, height * 0.76, palette.accent, 12, 180, 60);
          fx.text(width / 2, height * 0.74, `+${event.gain}`, palette.accent, 14);
        } else if (event.type === "near-miss") {
          feedbackQueue.push("score");
          say(`${event.close ? "CLOSE CALL" : "NEAR MISS"} +${event.gain}`, event.close ? palette.accent : palette.primary, event.close ? 17 : 15);
          fx.burst(width / 2 + event.side * width * 0.1, carY, palette.text, 8, 200, 0);
        } else if (event.type === "beat-swerve") {
          feedbackQueue.push("score");
          say(`ON THE BEAT +${event.gain}`, palette.accent, 15);
        } else if (event.type === "boost") {
          fx.banner("NITRO", "", palette.accent);
          fx.ring(width / 2, carY, palette.accent, width * 0.3, 0.5);
        } else if (event.type === "checkpoint") {
          feedbackQueue.push("level");
          fx.banner(`CHECKPOINT +${event.bonus}s`, event.intro ?? `Sector ${event.sector}: faster and busier`, palette.primary);
        } else if (event.type === "mission-done") {
          feedbackQueue.push("win");
          missionFlash = 2.2;
          fx.text(width / 2, height * 0.4, `MISSION DONE: ${missionRewardLabel(event.reward).toUpperCase()}`, palette.accent, 18);
          fx.burst(width / 2, height * 0.3, palette.accent, 16, 220, 40);
        } else if (event.type === "time-warning") {
          if (event.seconds <= 5) feedbackQueue.push("tap");
        } else if (event.type === "timeout") {
          feedbackQueue.push("crash");
          fx.banner("TIME UP", "Reach checkpoints to add time", palette.danger);
        }
      }
      if (!env.reducedMotion && state.boost > 0) {
        for (let i = 0; i < 2; i++) fx.trail(width / 2 + (rng() - 0.5) * width * 0.9, height * (0.5 + rng() * 0.45), palette.text, 0, height * 2.5);
      }
      fx.update(dt);
    },

    draw(ctx, audio) {
      ctx.save();
      fx.applyShake(ctx);
      if (view) view.render(ctx, audio, width, height);
      else flat?.render(ctx, audio, width, height);
      fx.draw(ctx);
      ctx.restore();

      if (env.quiet) return;
      drawHud(ctx, audio);
      fx.drawBanner(ctx, width, height);
      drawBeatFlash(ctx, width, height, palette.primary, audio.pulse * 0.12 + fx.flash * 0.6);
    },

    feedback() {
      return feedbackQueue.drain();
    },

    probe() {
      const near = state.position;
      const ahead = (z: number) => z > near && z - near < FOG_END;
      let threats = 0;
      for (const car of state.cars) {
        watcher.see(car.kind, car);
        if (!car.hit && ahead(car.z)) threats += 1;
      }
      for (const hazard of state.hazards) {
        watcher.see(hazard.kind, hazard);
        if (!hazard.hit && ahead(hazard.z)) threats += 1;
      }
      let pickups = 0;
      for (const pickup of state.pickups) {
        watcher.see("pickup", pickup);
        if (!pickup.taken && ahead(pickup.z)) pickups += 1;
      }
      const mission = state.mission;
      return {
        threats,
        pickups,
        spawned: watcher.spawned,
        damage: state.crashes + state.bumps,
        intensity: sim.intensity(),
        boss: false,
        mission: mission && !mission.done && !mission.failed ? `${sim.missionLabel(mission)} ${mission.progress}/${mission.target}` : null,
      };
    },

    status(): GameStatus {
      return { score: Math.floor(state.score), lives: state.lives, maxLives: MAX_LIVES, combo: state.combo, meter: null, over: state.over, level: state.sector, fever: state.boost > 0 };
    },

    dispose() {
      view?.dispose();
    },
  };
  return engine;

  // Time left and the mission under the score, speed and nitro bottom left,
  // the next checkpoint bottom right.
  function drawHud(ctx: Ctx2D, audio: AudioFrame) {
    const compact = width < 520;
    const top = compact ? 74 : 82;
    ctx.save();
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";

    const seconds = Math.ceil(state.timeLeft);
    const low = state.timeLeft < 10;
    const blink = low && Math.floor(state.time * 4) % 2 === 0;
    const timeW = compact ? 96 : 110;
    roundRect(ctx, width / 2 - timeW / 2, top, timeW, 30, 15);
    ctx.fillStyle = low ? "rgba(90,0,12,0.6)" : "rgba(0,0,0,0.45)";
    ctx.fill();
    ctx.font = "700 10px Poppins, sans-serif";
    ctx.fillStyle = withAlpha(palette.text, 0.7);
    ctx.fillText("TIME", width / 2 - timeW / 2 + 22, top + 15);
    ctx.font = "800 18px Hanson, Poppins, sans-serif";
    ctx.fillStyle = low ? (blink ? "#ff5a6e" : "#ffd0d6") : "#fff";
    ctx.fillText(String(seconds), width / 2 + 14, top + 16);

    const mission = state.mission;
    if (mission) {
      const label = sim.missionLabel(mission);
      const progress = mission.kind === "clean" || mission.kind === "roadworks" ? "" : `${mission.kind === "speed" ? mission.progress.toFixed(1) : mission.progress}/${mission.target}`;
      const reward = missionRewardLabel(mission.reward);
      const boxW = Math.min(width - 24, compact ? 300 : 340);
      const y = top + 38;
      const done = mission.done;
      roundRect(ctx, width / 2 - boxW / 2, y, boxW, 40, 12);
      ctx.fillStyle = done ? withAlpha(palette.accent, 0.22 + missionFlash * 0.1) : "rgba(0,0,0,0.45)";
      ctx.fill();
      ctx.font = "700 9px Poppins, sans-serif";
      ctx.textAlign = "left";
      ctx.fillStyle = withAlpha(palette.accent, 0.95);
      ctx.fillText(done ? "MISSION COMPLETE" : mission.failed ? "MISSION MISSED" : "MISSION", width / 2 - boxW / 2 + 12, y + 11);
      ctx.textAlign = "right";
      ctx.fillStyle = withAlpha(palette.text, 0.65);
      ctx.fillText(done ? reward : `Reward: ${reward}`, width / 2 + boxW / 2 - 12, y + 11);
      ctx.textAlign = "left";
      ctx.font = `600 ${compact ? 12 : 13}px Poppins, sans-serif`;
      ctx.fillStyle = mission.failed ? withAlpha(palette.text, 0.45) : "#fff";
      ctx.fillText(label, width / 2 - boxW / 2 + 12, y + 26);
      if (progress && !done) {
        ctx.textAlign = "right";
        ctx.font = "700 12px Poppins, sans-serif";
        ctx.fillStyle = withAlpha(palette.accent, 0.95);
        ctx.fillText(progress, width / 2 + boxW / 2 - 12, y + 26);
      }
      const share = done ? 1 : mission.kind === "clean" ? 0 : mission.progress / mission.target;
      ctx.fillStyle = "rgba(255,255,255,0.12)";
      ctx.fillRect(width / 2 - boxW / 2 + 12, y + 34, boxW - 24, 2);
      ctx.fillStyle = palette.accent;
      ctx.fillRect(width / 2 - boxW / 2 + 12, y + 34, (boxW - 24) * share, 2);
    }

    // A soft shade under the dashboard so it reads over cones and cars.
    const shade = ctx.createLinearGradient(0, height - 74, 0, height);
    shade.addColorStop(0, "rgba(0,0,0,0)");
    shade.addColorStop(1, "rgba(0,0,0,0.55)");
    ctx.fillStyle = shade;
    ctx.fillRect(0, height - 74, width, 74);

    // Nitro and speed.
    const gaugeWidth = Math.min(150, width * 0.34);
    const x = 14;
    const y = height - 16;
    const full = state.nitro >= 1 && state.boost <= 0;
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(x, y - 30, gaugeWidth, 6);
    ctx.fillStyle = full || state.boost > 0 ? palette.accent : withAlpha(palette.primary, 0.9);
    ctx.fillRect(x, y - 30, gaugeWidth * (state.boost > 0 ? state.boost / 3.2 : state.nitro), 6);
    if (full) drawGlow(ctx, env.art, palette.accent, x + gaugeWidth, y - 27, 16, 0.4 + audio.pulse * 0.3);
    ctx.font = "800 10px Poppins, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillStyle = withAlpha(palette.text, 0.85);
    ctx.fillText(full ? (env.keyHints ? "NITRO READY: SPACE" : "NITRO READY: TAP THE TOP") : "NITRO", x, y - 36);
    ctx.font = "800 15px Hanson, Poppins, sans-serif";
    ctx.fillStyle = withAlpha(palette.text, 0.95);
    ctx.fillText(`${sim.speedKmh()} KM/H`, x, y);

    // Sector and the distance to the next checkpoint.
    const toGo = Math.max(0, (state.sector * SECTOR_LENGTH - state.position) / 100000);
    const barW = Math.min(120, width * 0.28);
    ctx.textAlign = "right";
    ctx.font = "700 11px Poppins, sans-serif";
    ctx.fillStyle = withAlpha(palette.text, 0.8);
    ctx.fillText(`SECTOR ${state.sector}`, width - 14, y - 36);
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(width - 14 - barW, y - 30, barW, 6);
    ctx.fillStyle = withAlpha(palette.primary, 0.9);
    ctx.fillRect(width - 14 - barW, y - 30, barW * (1 - toGo / (SECTOR_LENGTH / 100000)), 6);
    ctx.font = "600 11px Poppins, sans-serif";
    ctx.fillStyle = withAlpha(palette.text, 0.75);
    ctx.fillText(`CHECKPOINT ${toGo.toFixed(1)} KM`, width - 14, y);
    ctx.restore();
  }
}

function roundRect(ctx: Ctx2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ---------------------------------------------------------------------------
// The 2D road: segment projection with a camera behind the car, used when
// WebGL is not available. Everything goes through one projection, so cars and
// hazards are drawn at exactly the size the rules use.

interface Building {
  x: number;
  w: number;
  h: number;
  lights: number[];
}

interface Row {
  x: number;
  y: number;
  // Pixels per world unit at this row.
  s: number;
  index: number;
}

interface ScreenPoint {
  x: number;
  y: number;
  s: number;
}

const ROW_BACK = 10;
const CAMERA_BACK = SEGMENT * 12;
const PEOPLE_COLORS = ["#ffd23f", "#4fc3f7", "#ff8a50", "#e8e8f0"];

export function createFlatRoad(env: GameEnv, sim: DriveSim) {
  const { theme, rng, art } = env;
  const { palette } = theme;
  const { state } = sim;
  const stars = createStars(rng, 70);
  const skyline: Building[] = Array.from({ length: 34 }, (_, i) => ({
    x: i / 34 + rng() * 0.01,
    w: 0.02 + rng() * 0.03,
    h: 0.05 + rng() * 0.16,
    lights: Array.from({ length: 6 }, () => rng()),
  }));
  const rows: Row[] = Array.from({ length: DRAW_DISTANCE + ROW_BACK }, () => ({ x: 0, y: 0, s: 0, index: 0 }));
  const drawables: Array<{ z: number; kind: number; ref: DriveCar | DriveHazard | null; id: number }> = [];
  let skyOffset = 0;
  let lastPosition = 0;
  let focal = 1;
  let cameraX = 0;
  let camY = 0;
  let horizon = 0;
  let rowPercent = 0;
  let halfW = 0;

  // Project a point on the road: lateral x in road half widths plus an extra
  // offset in world units, z in world units, h in world units above the road.
  const project = (x: number, z: number, h = 0, out: ScreenPoint = { x: 0, y: 0, s: 0 }): ScreenPoint | null => {
    const f = (z - state.position) / SEGMENT + ROW_BACK + rowPercent;
    const i = Math.floor(f);
    if (i < 0 || i >= rows.length - 1) return null;
    const a = rows[i];
    const b = rows[i + 1];
    const t = f - i;
    const s = a.s + (b.s - a.s) * t;
    out.s = s;
    out.x = a.x + (b.x - a.x) * t + x * ROAD_WIDTH * s;
    out.y = a.y + (b.y - a.y) * t - h * s;
    return out;
  };
  const scratch: ScreenPoint[] = Array.from({ length: 16 }, () => ({ x: 0, y: 0, s: 0 }));

  const fade = (z: number) => {
    const rel = z - state.position;
    const t = Math.min(1, Math.max(0, (rel - FOG_START) / (FOG_END - FOG_START)));
    return 1 - t * t * (3 - 2 * t);
  };

  // A box on the road, seen from behind: x, z its centre, yaw turning the nose
  // toward +x, sizes in world units. Draws the faces that face the camera.
  const corners = new Float64Array(16);
  const drawBox = (ctx: Ctx2D, cx: number, cz: number, yaw: number, hw: number, hl: number, bottom: number, top: number, colors: { back: string; side: string; top: string }) => {
    const sin = Math.sin(yaw);
    const cos = Math.cos(yaw);
    // Corner order: rear left, rear right, front right, front left.
    const local = [-hw, -hl, hw, -hl, hw, hl, -hw, hl];
    for (let k = 0; k < 4; k++) {
      const lx = local[k * 2];
      const lz = local[k * 2 + 1];
      const wx = cx * ROAD_WIDTH + lx * cos + lz * sin;
      const wz = cz - lx * sin + lz * cos;
      const low = project(wx / ROAD_WIDTH, wz, bottom, scratch[k]);
      const high = project(wx / ROAD_WIDTH, wz, top, scratch[k + 4]);
      if (!low || !high) return false;
      corners[k * 4] = low.x;
      corners[k * 4 + 1] = low.y;
      corners[k * 4 + 2] = high.x;
      corners[k * 4 + 3] = high.y;
    }
    const face = (a: number, b: number, color: string) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(corners[a * 4], corners[a * 4 + 1]);
      ctx.lineTo(corners[b * 4], corners[b * 4 + 1]);
      ctx.lineTo(corners[b * 4 + 2], corners[b * 4 + 3]);
      ctx.lineTo(corners[a * 4 + 2], corners[a * 4 + 3]);
      ctx.closePath();
      ctx.fill();
    };
    // A side shows when its far edge sits outside its near edge on screen.
    if (corners[3 * 4] > corners[0]) face(0, 3, colors.side);
    if (corners[2 * 4] < corners[1 * 4]) face(1, 2, colors.side);
    ctx.fillStyle = colors.top;
    ctx.beginPath();
    ctx.moveTo(corners[2], corners[3]);
    ctx.lineTo(corners[6], corners[7]);
    ctx.lineTo(corners[10], corners[11]);
    ctx.lineTo(corners[14], corners[15]);
    ctx.closePath();
    ctx.fill();
    face(0, 1, colors.back);
    return true;
  };

  const shade = (color: string, amount: number) => withAlpha(color, amount);

  const drawCar = (ctx: Ctx2D, x: number, z: number, yaw: number, roll: number, hw: number, hl: number, height: number, color: string, alpha: number, isPlayer: boolean, audio: AudioFrame, indicator = 0, braking = false) => {
    if (alpha <= 0.02) return;
    ctx.save();
    ctx.globalAlpha = alpha;
    const base = project(x, z - hl, 0, scratch[8]);
    if (base && roll) {
      ctx.translate(base.x, base.y);
      ctx.rotate(roll);
      ctx.translate(-base.x, -base.y);
    }
    const body = { back: color, side: shade(color, 0.55), top: shade(color, 0.8) };
    if (!drawBox(ctx, x, z, yaw, hw * ROAD_WIDTH, hl, 40, height * 0.5, body)) {
      ctx.restore();
      return;
    }
    // Cabin and glass.
    drawBox(ctx, x + Math.sin(yaw) * hl * 0.05 / ROAD_WIDTH, z - hl * 0.05, yaw, hw * ROAD_WIDTH * 0.8, hl * 0.45, height * 0.5, height, { back: "rgba(12,12,20,0.95)", side: "rgba(20,20,32,0.95)", top: shade(color, 0.7) });
    // Tail lights on the rear face.
    const rear = Math.cos(yaw);
    const lx = (off: number) => project(x + (off * Math.cos(yaw) * hw * ROAD_WIDTH - hl * Math.sin(yaw)) / ROAD_WIDTH, z - hl * rear + off * Math.sin(yaw) * hw * ROAD_WIDTH, height * 0.36, scratch[9]);
    for (const off of [-0.7, 0.7]) {
      const p = lx(off);
      if (!p) continue;
      const w = hw * ROAD_WIDTH * 0.42 * p.s;
      const h = Math.max(1.5, height * 0.07 * p.s);
      const blinking = indicator !== 0 && Math.sign(off) === indicator;
      ctx.fillStyle = blinking ? "#ffb000" : braking ? "#ff5a70" : "#d8284a";
      ctx.fillRect(p.x - w / 2, p.y - h / 2, w, h);
      if (blinking) drawGlow(ctx, art, "hsl(40 100% 55%)", p.x, p.y, w * 1.2, 0.6 * alpha);
      else if (isPlayer || alpha > 0.5) drawGlow(ctx, art, "hsl(350 100% 60%)", p.x, p.y, w * (0.8 + audio.bass * 0.2), (braking ? 0.5 : 0.25) * alpha);
    }
    ctx.restore();
  };

  const drawCone = (ctx: Ctx2D, x: number, z: number, lift: number, tilt: number, alpha: number) => {
    const p = project(x, z, lift * 200, scratch[10]);
    if (!p) return;
    const r = 130 * p.s;
    const h = 340 * p.s;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(p.x, p.y);
    if (tilt) ctx.rotate(tilt);
    ctx.fillStyle = "#1a1a1a";
    ctx.fillRect(-r * 1.2, -h * 0.08, r * 2.4, h * 0.08);
    ctx.fillStyle = "#ff6a1a";
    ctx.beginPath();
    ctx.moveTo(-r, -h * 0.06);
    ctx.lineTo(-r * 0.18, -h);
    ctx.lineTo(r * 0.18, -h);
    ctx.lineTo(r, -h * 0.06);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fillRect(-r * 0.62, -h * 0.55, r * 1.24, h * 0.14);
    ctx.restore();
  };

  const drawBarrierBoard = (ctx: Ctx2D, x: number, z: number, hw: number, alpha: number, blink: boolean) => {
    const left = project(x - hw, z, 60, scratch[11]);
    const right = project(x + hw, z, 60, scratch[12]);
    if (!left || !right) return;
    const h = 200 * left.s;
    const w = right.x - left.x;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = "#222";
    const legW = Math.max(1, 30 * left.s);
    ctx.fillRect(left.x + w * 0.12, left.y - 4, legW, 60 * left.s + 4);
    ctx.fillRect(right.x - w * 0.12 - legW, right.y - 4, legW, 60 * left.s + 4);
    ctx.beginPath();
    ctx.rect(left.x, left.y - h, w, h);
    ctx.fillStyle = "#f4f4f4";
    ctx.fill();
    ctx.save();
    ctx.clip();
    ctx.fillStyle = "#e0203a";
    const stripe = Math.max(4, h * 0.9);
    for (let sx = left.x - h; sx < right.x + h; sx += stripe * 2) {
      ctx.beginPath();
      ctx.moveTo(sx, left.y);
      ctx.lineTo(sx + stripe, left.y);
      ctx.lineTo(sx + stripe + h, left.y - h);
      ctx.lineTo(sx + h, left.y - h);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
    const lampR = Math.max(1.5, 26 * left.s);
    ctx.fillStyle = blink ? "#ffb300" : "#6a4a00";
    for (const lampX of [left.x + lampR * 2, right.x - lampR * 2]) {
      ctx.beginPath();
      ctx.arc(lampX, left.y - h - lampR, lampR, 0, Math.PI * 2);
      ctx.fill();
      if (blink) drawGlow(ctx, art, "hsl(40 100% 55%)", lampX, left.y - h - lampR, lampR * 4, 0.5 * alpha);
    }
    ctx.restore();
  };

  const drawRoadworks = (ctx: Ctx2D, hazard: DriveHazard, blink: boolean) => {
    const z0 = hazard.z - hazard.halfLength;
    const z1 = hazard.z + hazard.halfLength;
    const from = Math.max(z0, state.position - SEGMENT * 4);
    const to = Math.min(z1, state.position + FOG_END);
    // The closed lane, darker, between two rows of cones.
    const a = project(hazard.x - hazard.halfWidth, from, 0, scratch[13]);
    const b = project(hazard.x + hazard.halfWidth, from, 0, scratch[14]);
    const c = project(hazard.x + hazard.halfWidth, to, 0, scratch[15]);
    const d = project(hazard.x - hazard.halfWidth, to, 0, scratch[7]);
    if (a && b && c && d) {
      ctx.fillStyle = "rgba(60,40,0,0.35)";
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineTo(c.x, c.y);
      ctx.lineTo(d.x, d.y);
      ctx.closePath();
      ctx.fill();
    }
    // Cones on both edges and a board every few metres, far to near.
    for (let z = z1 - ((z1 - z0) % 500); z >= z0; z -= 500) {
      if (z < from || z > to) continue;
      const alpha = fade(z);
      for (const side of [-1, 1]) drawCone(ctx, hazard.x + side * (hazard.halfWidth - 0.03), z, 0, 0, alpha);
      if (Math.round((z - z0) / 500) % 4 === 2) drawBarrierBoard(ctx, hazard.x, z, hazard.halfWidth * 0.75, alpha, blink);
    }
    if (z0 >= from && z0 <= to) drawBarrierBoard(ctx, hazard.x, z0, hazard.halfWidth, fade(z0), blink);
  };

  const drawSlick = (ctx: Ctx2D, hazard: DriveHazard, alpha: number) => {
    const near = project(hazard.x, hazard.z - hazard.halfLength, 0, scratch[10]);
    const far = project(hazard.x, hazard.z + hazard.halfLength, 0, scratch[11]);
    if (!near || !far) return;
    const cx = (near.x + far.x) / 2;
    const cy = (near.y + far.y) / 2;
    const rx = hazard.halfWidth * ROAD_WIDTH * (near.s + far.s) * 0.5;
    const ry = Math.max(2, (near.y - far.y) / 2);
    ctx.save();
    ctx.globalAlpha = alpha;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rx);
    if (hazard.variant === 0) {
      g.addColorStop(0, "rgba(10,10,14,0.95)");
      g.addColorStop(0.6, "rgba(40,20,70,0.85)");
      g.addColorStop(0.8, "rgba(20,90,90,0.6)");
      g.addColorStop(1, "rgba(10,10,14,0)");
    } else {
      g.addColorStop(0, "rgba(120,170,230,0.55)");
      g.addColorStop(0.7, "rgba(60,90,150,0.45)");
      g.addColorStop(1, "rgba(40,60,120,0)");
    }
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  const drawPerson = (ctx: Ctx2D, hazard: DriveHazard, alpha: number) => {
    const p = project(hazard.x, hazard.z, 0, scratch[10]);
    if (!p) return;
    const h = 700 * p.s;
    const walk = Math.sin(hazard.phase);
    const dir = Math.sign(hazard.vx) || 1;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(p.x, p.y);
    ctx.lineCap = "round";
    // Legs and arms swing as they walk.
    ctx.strokeStyle = "#20202a";
    ctx.lineWidth = Math.max(1.5, h * 0.09);
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(0, -h * 0.46);
      ctx.lineTo(s * walk * h * 0.14, 0);
      ctx.stroke();
    }
    const coat = PEOPLE_COLORS[hazard.variant % PEOPLE_COLORS.length];
    ctx.fillStyle = coat;
    roundRect(ctx, -h * 0.13, -h * 0.84, h * 0.26, h * 0.42, h * 0.06);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fillRect(-h * 0.13, -h * 0.58, h * 0.26, h * 0.04);
    ctx.strokeStyle = coat;
    ctx.lineWidth = Math.max(1.2, h * 0.07);
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(s * h * 0.12, -h * 0.8);
      ctx.lineTo(s * h * 0.14 - walk * s * h * 0.08 + (hazard.hit ? s * h * 0.12 : 0), hazard.hit ? -h * 1.02 : -h * 0.5);
      ctx.stroke();
    }
    ctx.fillStyle = "#e8c4a0";
    ctx.beginPath();
    ctx.arc(dir * h * 0.01, -h * 0.93, h * 0.09, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  const drawSign = (ctx: Ctx2D, z: number, side: number, kind: string, alpha: number) => {
    const foot = project(side * 1.5, z, 0, scratch[10]);
    const head = project(side * 1.5, z, 1150, scratch[11]);
    if (!foot || !head) return;
    const size = 380 * foot.s;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = "#555";
    ctx.lineWidth = Math.max(1, 40 * foot.s);
    ctx.beginPath();
    ctx.moveTo(foot.x, foot.y);
    ctx.lineTo(head.x, head.y);
    ctx.stroke();
    ctx.translate(head.x, head.y);
    if (kind === "crosswalk") {
      ctx.fillStyle = "#1e5bd8";
      ctx.fillRect(-size / 2, -size / 2, size, size);
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.moveTo(0, -size * 0.4);
      ctx.lineTo(size * 0.42, size * 0.36);
      ctx.lineTo(-size * 0.42, size * 0.36);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#111";
      ctx.beginPath();
      ctx.arc(0, -size * 0.1, size * 0.06, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(-size * 0.04, -size * 0.04, size * 0.08, size * 0.2);
      ctx.fillRect(-size * 0.2, size * 0.24, size * 0.4, size * 0.05);
    } else {
      ctx.rotate(Math.PI / 4);
      ctx.fillStyle = "#ffb000";
      ctx.fillRect(-size * 0.36, -size * 0.36, size * 0.72, size * 0.72);
      ctx.strokeStyle = "#111";
      ctx.lineWidth = Math.max(1, size * 0.04);
      ctx.strokeRect(-size * 0.32, -size * 0.32, size * 0.64, size * 0.64);
      ctx.rotate(-Math.PI / 4);
      ctx.fillStyle = "#111";
      if (kind === "merge") {
        ctx.beginPath();
        ctx.moveTo(-side * size * 0.2, size * 0.25);
        ctx.lineTo(-side * size * 0.2, -size * 0.05);
        ctx.lineTo(side * size * 0.05, -size * 0.2);
        ctx.lineWidth = Math.max(1, size * 0.08);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.arc(0, -size * 0.14, size * 0.06, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(-size * 0.05, -size * 0.07, size * 0.1, size * 0.2);
        ctx.fillRect(-size * 0.2, size * 0.14, size * 0.4, size * 0.05);
      }
    }
    ctx.restore();
  };

  const drawCrosswalk = (ctx: Ctx2D, z: number, alpha: number) => {
    ctx.save();
    ctx.globalAlpha = alpha * 0.85;
    ctx.fillStyle = "#e9e9ef";
    for (let bar = -0.95; bar < 0.95; bar += 0.2) {
      const a = project(bar, z - 260, 0, scratch[10]);
      const b = project(bar + 0.11, z - 260, 0, scratch[11]);
      const c = project(bar + 0.11, z + 260, 0, scratch[12]);
      const d = project(bar, z + 260, 0, scratch[13]);
      if (!a || !b || !c || !d) continue;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineTo(c.x, c.y);
      ctx.lineTo(d.x, d.y);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  };

  const drawCheckpoint = (ctx: Ctx2D, z: number, alpha: number) => {
    const left = project(-1.3, z, 1500, scratch[10]);
    const right = project(1.3, z, 1500, scratch[11]);
    const leftFoot = project(-1.3, z, 0, scratch[12]);
    const rightFoot = project(1.3, z, 0, scratch[13]);
    if (!left || !right || !leftFoot || !rightFoot) return;
    const h = 320 * left.s;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = "#333";
    ctx.lineWidth = Math.max(1.5, 60 * left.s);
    ctx.beginPath();
    ctx.moveTo(leftFoot.x, leftFoot.y);
    ctx.lineTo(left.x, left.y);
    ctx.moveTo(rightFoot.x, rightFoot.y);
    ctx.lineTo(right.x, right.y);
    ctx.stroke();
    ctx.fillStyle = "rgba(0,0,0,0.75)";
    ctx.fillRect(left.x, left.y - h, right.x - left.x, h);
    ctx.fillStyle = withAlpha(palette.accent, 0.9);
    ctx.fillRect(left.x, left.y - h, right.x - left.x, Math.max(1, h * 0.08));
    ctx.fillRect(left.x, left.y - Math.max(1, h * 0.08), right.x - left.x, Math.max(1, h * 0.08));
    ctx.font = `800 ${Math.max(6, h * 0.5)}px Hanson, Poppins, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#fff";
    ctx.fillText("CHECKPOINT", (left.x + right.x) / 2, left.y - h / 2);
    ctx.restore();
  };

  const drawNote = (ctx: Ctx2D, x: number, y: number, size: number) => {
    ctx.save();
    ctx.fillStyle = "#fff";
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = Math.max(1.5, size * 0.14);
    ctx.beginPath();
    ctx.ellipse(x - size * 0.2, y + size * 0.3, size * 0.32, size * 0.24, -0.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x + size * 0.1, y + size * 0.28);
    ctx.lineTo(x + size * 0.1, y - size * 0.5);
    ctx.quadraticCurveTo(x + size * 0.5, y - size * 0.3, x + size * 0.42, y - size * 0.02);
    ctx.stroke();
    ctx.restore();
  };

  return {
    render(ctx: Ctx2D, audio: AudioFrame, width: number, height: number) {
      const aspect = width / Math.max(1, height);
      horizon = height * (aspect < 1 ? 0.4 : 0.44);
      // The road at the car is this many pixels from the middle to the edge.
      const roadAtCar = width * 0.78 * Math.min(0.85, Math.max(0.4, 0.85 - (aspect - 0.5) * 0.28));
      const carDepth = PLAYER_AHEAD + CAMERA_BACK;
      focal = (roadAtCar * carDepth) / ROAD_WIDTH;
      const rearDepth = carDepth - PLAYER_SIZE.halfLength;
      const carBottom = height * 0.9;
      camY = ((carBottom - horizon) * rearDepth) / focal;
      halfW = width / 2;
      cameraX = state.playerX * ROAD_WIDTH * 0.85;

      const baseIndex = Math.floor(state.position / SEGMENT);
      const percent = (state.position % SEGMENT) / SEGMENT;
      skyOffset += sim.curveAt(baseIndex) * ((state.position - lastPosition) / (SEGMENT * 58)) * 0.004;
      lastPosition = state.position;
      const hill0 = sim.hillAt(baseIndex) + (sim.hillAt(baseIndex + 1) - sim.hillAt(baseIndex)) * percent;
      let x = 0;
      let dx = -sim.curveAt(baseIndex) * percent;
      for (let r = 0; r < rows.length; r++) {
        const n = r - ROW_BACK;
        const depth = Math.max(40, (n - percent) * SEGMENT + CAMERA_BACK);
        const s = focal / depth;
        const row = rows[r];
        row.index = baseIndex + n;
        row.s = s;
        row.x = halfW + ((n <= 0 ? 0 : x) - cameraX) * s;
        row.y = horizon + (camY - (sim.hillAt(baseIndex + n) - hill0)) * s;
        if (n >= 0) {
          x += dx;
          dx += sim.curveAt(baseIndex + n);
        }
      }
      rowPercent = percent;

      // Sky, stars, sun and skyline.
      const sky = ctx.createLinearGradient(0, 0, 0, horizon);
      sky.addColorStop(0, palette.backgroundDeep);
      sky.addColorStop(0.6, palette.background);
      sky.addColorStop(1, withAlpha(palette.secondary, 0.45));
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, width, height);
      drawStars(ctx, stars, width, horizon * 0.8, state.time, palette.text, { x: skyOffset * 0.5, y: 0 }, audio.treble * 0.5);
      const sunR = Math.min(width, height) * 0.18;
      ctx.save();
      ctx.globalAlpha = 0.75;
      drawCoverSun(ctx, art, palette, width / 2 - ((skyOffset * 800) % width) * 0.05, horizon - sunR * 0.45, sunR, state.time * 0.4, audio.pulse * 0.4);
      ctx.restore();
      for (const layer of [0, 1]) {
        const shift = (((skyOffset * (layer === 0 ? 400 : 900)) % 1) + 1) % 1;
        for (const building of skyline) {
          const bx = ((building.x + shift + layer * 0.013) % 1) * (width * 1.1) - width * 0.05;
          const bw = building.w * width;
          const bh = building.h * height * (layer === 0 ? 0.8 : 1.15);
          ctx.fillStyle = layer === 0 ? withAlpha(palette.backgroundDeep, 0.75) : palette.backgroundDeep;
          ctx.fillRect(bx, horizon - bh, bw, bh + 2);
          if (layer === 1) {
            for (let l = 0; l < building.lights.length; l++) {
              if (Math.sin(state.time * 1.5 + building.lights[l] * 30) + audio.treble * 0.5 <= 0.6) continue;
              ctx.fillStyle = l % 2 ? palette.accent : palette.primary;
              ctx.globalAlpha = 0.45;
              ctx.fillRect(bx + bw * (0.2 + (l % 2) * 0.45), horizon - bh + bh * (0.15 + Math.floor(l / 2) * 0.25), Math.max(1.5, bw * 0.18), Math.max(1.5, bh * 0.06));
              ctx.globalAlpha = 1;
            }
          }
        }
      }
      const fogColor = palette.background;
      ctx.fillStyle = palette.backgroundDeep;
      ctx.fillRect(0, horizon, width, height - horizon);

      // Road, far to near, with the fog thickening into the distance.
      let maxY = height;
      const visible: number[] = [];
      for (let r = 0; r < rows.length - 1; r++) {
        const near = rows[r];
        const far = rows[r + 1];
        if (far.y >= maxY || near.y <= far.y - 0.5) continue;
        if (near.y < 0) break;
        visible.push(r);
        maxY = far.y;
      }
      for (let v = visible.length - 1; v >= 0; v--) {
        const near = rows[visible[v]];
        const far = rows[visible[v] + 1];
        const rel = (near.index - baseIndex) * SEGMENT;
        const fogT = Math.min(1, Math.max(0, (rel - FOG_START) / (FOG_END - FOG_START)));
        const alt = near.index % 2 === 0;
        ctx.fillStyle = alt ? palette.background : palette.backgroundDeep;
        ctx.fillRect(0, far.y, width, near.y - far.y + 1);
        const w1 = ROAD_WIDTH * near.s;
        const w2 = ROAD_WIDTH * far.s;
        const quad = (o: number, hw: number, color: string) => {
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(near.x + o * w1 - hw * w1, near.y);
          ctx.lineTo(far.x + o * w2 - hw * w2, far.y);
          ctx.lineTo(far.x + o * w2 + hw * w2, far.y);
          ctx.lineTo(near.x + o * w1 + hw * w1, near.y);
          ctx.closePath();
          ctx.fill();
        };
        quad(0, 1.14, alt ? withAlpha(palette.primary, 0.35 + audio.pulse * 0.15) : withAlpha(palette.secondary, 0.25));
        quad(0, 1, alt ? "#121219" : "#0e0e14");
        if (alt) for (const lane of [-0.31, 0.31]) quad(lane, 0.012, "rgba(230,230,240,0.55)");
        if (fogT > 0.02) {
          ctx.fillStyle = withAlpha(fogColor, fogT * 0.92);
          ctx.fillRect(0, far.y, width, near.y - far.y + 1);
        }
        if (near.index % 8 === 0 && fogT < 0.95) {
          for (const side of [-1, 1]) {
            const px = near.x + side * w1 * 1.32;
            const poleH = 1600 * near.s;
            ctx.strokeStyle = withAlpha(palette.text, 0.3 * (1 - fogT));
            ctx.lineWidth = Math.max(1, 50 * near.s);
            ctx.beginPath();
            ctx.moveTo(px, near.y);
            ctx.lineTo(px, near.y - poleH);
            ctx.stroke();
            drawGlow(ctx, art, palette.accent, px, near.y - poleH, Math.max(4, poleH * 0.18), 0.5 * (1 - fogT));
          }
        }
      }

      // Things flat on the road go first, then everything standing, far to near.
      const blink = Math.floor(state.time * 2.5) % 2 === 0;
      for (const walk of state.crosswalks) drawCrosswalk(ctx, walk.z, fade(walk.z));
      for (const hazard of state.hazards) if (hazard.kind === "slick") drawSlick(ctx, hazard, fade(hazard.z));
      const nextGate = state.sector * SECTOR_LENGTH;

      drawables.length = 0;
      for (const car of state.cars) drawables.push({ z: car.z - car.halfLength, kind: 0, ref: car, id: 0 });
      for (const hazard of state.hazards) if (hazard.kind !== "slick") drawables.push({ z: hazard.kind === "roadworks" ? hazard.z + hazard.halfLength : hazard.z, kind: 1, ref: hazard, id: 0 });
      // Notes left behind are not drawn: they would sweep past the camera huge.
      const carRear = state.position + PLAYER_AHEAD - PLAYER_SIZE.halfLength;
      for (let i = 0; i < state.pickups.length; i++) if (state.pickups[i].z > carRear) drawables.push({ z: state.pickups[i].z, kind: 2, ref: null, id: i });
      for (let i = 0; i < state.signs.length; i++) drawables.push({ z: state.signs[i].z, kind: 3, ref: null, id: i });
      drawables.push({ z: nextGate, kind: 4, ref: null, id: 0 });
      drawables.push({ z: state.position + PLAYER_AHEAD - PLAYER_SIZE.halfLength - 1, kind: 5, ref: null, id: 0 });
      drawables.sort((a, b) => b.z - a.z);

      for (const item of drawables) {
        if (item.kind === 0) {
          const car = item.ref as DriveCar;
          const height3d = car.kind === "truck" ? 1150 : car.kind === "racer" ? 480 : 600;
          const indicator = car.signal > 0 && Math.floor(state.time * 5) % 2 === 0 ? Math.sign(LANES[car.signalLane] - car.x) : 0;
          drawCar(ctx, car.x, car.z, car.yaw, 0, car.halfWidth, car.halfLength, height3d, car.color, fade(car.z) * (car.hit ? 0.7 : 1), false, audio, indicator, car.accel < -800);
        } else if (item.kind === 1) {
          const hazard = item.ref as DriveHazard;
          const alpha = fade(hazard.z);
          if (hazard.kind === "cone") drawCone(ctx, hazard.x, hazard.z, hazard.lift, hazard.hit ? hazard.phase * 0.6 * (Math.sign(hazard.vx) || 1) : 0, alpha);
          else if (hazard.kind === "barrier") drawBarrierBoard(ctx, hazard.x, hazard.z, hazard.halfWidth, alpha, blink);
          else if (hazard.kind === "roadworks") drawRoadworks(ctx, hazard, blink);
          else if (hazard.kind === "pedestrian") drawPerson(ctx, hazard, alpha);
        } else if (item.kind === 2) {
          const pickup = state.pickups[item.id];
          const p = project(pickup.x, pickup.z, 450, scratch[10]);
          if (!p) continue;
          const size = Math.max(4, 260 * p.s);
          ctx.save();
          ctx.globalAlpha = fade(pickup.z);
          drawGlow(ctx, art, palette.accent, p.x, p.y, size * 1.6, 0.6);
          drawNote(ctx, p.x, p.y, size);
          ctx.restore();
        } else if (item.kind === 3) {
          const sign = state.signs[item.id];
          drawSign(ctx, sign.z, sign.side, sign.kind, fade(sign.z));
        } else if (item.kind === 4) {
          drawCheckpoint(ctx, nextGate, fade(nextGate));
        } else {
          const player = playerFootprint(state);
          const flashing = state.invulnerable > 0 && Math.floor(state.invulnerable * 12) % 2 === 0;
          drawCar(ctx, player.x / ROAD_WIDTH, player.z, state.yaw, state.roll, PLAYER_SIZE.halfWidth, PLAYER_SIZE.halfLength, 600, palette.primary, flashing ? 0.35 : 1, true, audio);
          if (state.boost > 0) {
            const p = project(player.x / ROAD_WIDTH, player.z - PLAYER_SIZE.halfLength, 140, scratch[10]);
            if (p) for (const side of [-1, 1]) drawGlow(ctx, art, palette.accent, p.x + side * 220 * p.s, p.y, 180 * p.s, 0.6);
          }
        }
      }
    },
  };
}
